import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Upload, Link2, PlusCircle, EyeOff, Undo2, Trash2, Landmark } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate, ugx } from "@/lib/format";
import { parseCsv } from "@/lib/csv";
import { PageHeader, Panel, Empty, StatusPill } from "@/components/Shell";

const NONE = "__none__";
const TEMPLATES = {
  generic: { label: "Generic CSV (map columns yourself)", hints: {} },
  mtn: { label: "MTN Mobile Money statement", hints: { date: ["date", "transaction date"], description: ["transaction type", "type", "details", "from/to", "to/from", "name"], reference: ["transaction id", "txn id", "id", "reference"], amount_in: ["credit", "amount in", "money in", "received"], amount_out: ["debit", "amount out", "money out", "sent"], amount: ["amount"] } },
  airtel: { label: "Airtel Money statement", hints: { date: ["date", "trans date", "transaction date"], description: ["description", "transaction type", "details", "counterparty", "to/from"], reference: ["transaction id", "trans id", "txn id", "reference"], amount_in: ["credit", "amount in", "cr"], amount_out: ["debit", "amount out", "dr"], amount: ["amount"] } },
  bank: { label: "Bank statement (Stanbic, Centenary, dfcu, Equity…)", hints: { date: ["date", "value date", "transaction date", "posting date"], description: ["description", "narration", "details", "particulars", "transaction details"], reference: ["reference", "ref", "cheque no", "transaction ref"], amount_in: ["credit", "deposit", "credit amount", "money in"], amount_out: ["debit", "withdrawal", "debit amount", "money out"], amount: ["amount"] } },
};
const FIELDS = [["date", "Date"], ["description", "Description"], ["reference", "Reference / Txn ID"], ["amount_in", "Money in"], ["amount_out", "Money out"], ["amount", "Signed amount (if single column)"]];

const guess = (headers, hints) => {
  const map = {};
  const low = headers.map((h) => h.trim().toLowerCase());
  FIELDS.forEach(([f]) => {
    const cands = hints[f] || TEMPLATES.bank.hints[f] || [];
    const idx = low.findIndex((h) => cands.includes(h)) !== -1 ? low.findIndex((h) => cands.includes(h)) : low.findIndex((h) => cands.some((c) => h.includes(c)));
    map[f] = idx >= 0 ? String(idx) : NONE;
  });
  return map;
};

const num = (v) => { const n = Number(String(v ?? "").replace(/[^0-9.-]/g, "")); return Number.isFinite(n) ? n : 0; };
const isoDate = (v) => {
  const s = String(v || "").trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/);
  if (m) { const y = m[3].length === 2 ? `20${m[3]}` : m[3]; return `${y}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`; }
  const d = new Date(s); return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

export default function Banking() {
  const { canWrite } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("unmatched");
  const [importOpen, setImportOpen] = useState(false);
  const [template, setTemplate] = useState("generic");
  const [csv, setCsv] = useState(null);
  const [mapping, setMapping] = useState({});
  const [createFor, setCreateFor] = useState(null);
  const [createForm, setCreateForm] = useState({ kind: "expense", ref_id: "", account_id: "", vat_rate: 0 });
  const [openDocs, setOpenDocs] = useState({ invoices: [], bills: [] });

  useEffect(() => {
    api.get("/accounts").then(({ data }) => {
      const cash = data.filter((a) => ["1000", "1010", "1020"].includes(a.code));
      setAccounts(cash); setExpenseAccounts(data.filter((a) => a.type === "expense"));
      if (cash.length && !accountId) setAccountId(cash.find((a) => a.code === "1010")?.id || cash[0].id);
    });
    /* eslint-disable-next-line */
  }, []);

  const load = () => {
    if (!accountId) return;
    api.get("/bank/transactions", { params: { account_id: accountId } }).then(({ data }) => setTxns(data));
    api.get("/bank/summary", { params: { account_id: accountId } }).then(({ data }) => setSummary(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);

  const onFile = async (file) => {
    if (!file) return;
    const rows = parseCsv(await file.text());
    if (rows.length < 2) return toast.error("This file has no data rows");
    setCsv({ headers: rows[0], rows: rows.slice(1), name: file.name });
    setMapping(guess(rows[0], TEMPLATES[template].hints));
  };

  const preview = useMemo(() => {
    if (!csv) return [];
    const col = (f, r) => (mapping[f] && mapping[f] !== NONE ? r[Number(mapping[f])] : "");
    return csv.rows.map((r) => {
      let amount = 0;
      if (mapping.amount && mapping.amount !== NONE) amount = num(col("amount", r));
      else amount = num(col("amount_in", r)) - num(col("amount_out", r));
      return { date: isoDate(col("date", r)), description: col("description", r) || "", reference: col("reference", r) || null, amount };
    }).filter((r) => r.date && r.amount);
  }, [csv, mapping]);

  const doImport = async () => {
    try {
      const { data } = await api.post("/bank/import", { account_id: accountId, source: template, rows: preview });
      const parts = [`${data.imported} lines imported`];
      if (data.skipped_duplicates) parts.push(`${data.skipped_duplicates} duplicates skipped`);
      if (data.skipped_zero_amount) parts.push(`${data.skipped_zero_amount} zero-amount lines ignored`);
      toast.success(parts.join(", "));
      setImportOpen(false); setCsv(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const act = async (t, path, body) => {
    try { await api.post(`/bank/transactions/${t.id}/${path}`, body || {}); toast.success("Updated"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const applySuggestion = (t) => t.suggestion.action === "match"
    ? act(t, "match", { kind: t.suggestion.kind, ref_id: t.suggestion.ref_id })
    : act(t, "create", { kind: t.suggestion.kind, ref_id: t.suggestion.ref_id });

  const openCreate = async (t) => {
    const [inv, bills] = await Promise.all([api.get("/invoices"), api.get("/bills")]);
    setOpenDocs({ invoices: inv.data.filter((i) => i.amount_due > 0.01), bills: bills.data.filter((b) => b.amount_due > 0.01) });
    setCreateForm({ kind: t.amount > 0 ? "invoice_payment" : "expense", ref_id: "", account_id: "", vat_rate: 0 });
    setCreateFor(t);
  };
  const submitCreate = async (e) => {
    e.preventDefault();
    await act(createFor, "create", { ...createForm, vat_rate: Number(createForm.vat_rate) });
    setCreateFor(null);
  };

  const shown = txns.filter((t) => filter === "all" || t.status === filter);
  const acc = accounts.find((a) => a.id === accountId);

  return (
    <div data-testid="banking-page">
      <PageHeader title="Banking & Mobile Money" subtitle="Import bank or MoMo statements, match each line to your books and reconcile."
        action={canWrite && (
          <button data-testid="import-statement-button" onClick={() => setImportOpen(true)}
            className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            <Upload size={16} /> Import statement
          </button>
        )} />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Landmark size={18} className="text-slate-500" />
        <select data-testid="bank-account-select" value={accountId} onChange={(e) => setAccountId(e.target.value)}
          className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
        </select>
        <div className="flex gap-1 rounded-md border border-slate-200 bg-white p-1 text-xs font-semibold">
          {["unmatched", "matched", "excluded", "all"].map((f) => (
            <button key={f} data-testid={`bank-filter-${f}`} onClick={() => setFilter(f)}
              className={`rounded px-3 py-1 capitalize ${filter === f ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{f}</button>
          ))}
        </div>
      </div>

      {summary && (
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="bank-summary">
          {[["Book balance", ugx(summary.book_balance)], ["Statement in", ugx(summary.statement_in)], ["Statement out", ugx(summary.statement_out)],
            ["To reconcile", `${summary.unmatched} lines · ${money(summary.unmatched_amount)}`], ["Matched", `${summary.matched} of ${summary.total}`]].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500">{l}</p>
              <p className="mt-1 font-display text-base font-bold text-slate-900">{v}</p>
            </div>
          ))}
        </div>
      )}

      <Panel title={`${shown.length} statement lines · ${acc?.name || ""}`}>
        {shown.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="bank-transactions-table">
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-5 py-2.5">Date</th><th className="px-5 py-2.5">Description</th><th className="px-5 py-2.5">Ref</th>
                  <th className="px-5 py-2.5 text-right">In</th><th className="px-5 py-2.5 text-right">Out</th>
                  <th className="px-5 py-2.5">Status</th><th className="px-5 py-2.5">Suggested match</th><th className="px-5 py-2.5 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {shown.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-3 text-slate-600">{shortDate(t.date)}</td>
                    <td className="max-w-xs truncate px-5 py-3 text-slate-800" title={t.description}>{t.description || "—"}</td>
                    <td className="px-5 py-3 text-xs text-slate-500">{t.reference || "—"}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-emerald-700">{t.amount > 0 ? money(t.amount) : ""}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-rose-700">{t.amount < 0 ? money(-t.amount) : ""}</td>
                    <td className="px-5 py-3"><StatusPill status={t.status} /></td>
                    <td className="px-5 py-3 text-xs text-slate-600">
                      {t.suggestion ? <span data-testid="bank-suggestion">{t.suggestion.label}</span> : t.matched_kind ? <span className="capitalize">{t.matched_kind.replace("_", " ")}</span> : "—"}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {canWrite && (
                        <div className="flex justify-end gap-2">
                          {t.status === "unmatched" && t.suggestion && (
                            <button data-testid="apply-suggestion-button" onClick={() => applySuggestion(t)}
                              className="flex items-center gap-1 rounded bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white hover:bg-emerald-700">
                              <Link2 size={12} /> {t.suggestion.action === "match" ? "Match" : "Record"}
                            </button>
                          )}
                          {t.status === "unmatched" && (
                            <>
                              <button data-testid="record-transaction-button" onClick={() => openCreate(t)} className="text-slate-400 hover:text-emerald-600" aria-label="Record as…"><PlusCircle size={15} /></button>
                              <button data-testid="exclude-transaction-button" onClick={() => act(t, "exclude")} className="text-slate-400 hover:text-amber-600" aria-label="Exclude"><EyeOff size={15} /></button>
                            </>
                          )}
                          {t.status !== "unmatched" && (
                            <button data-testid="unmatch-transaction-button" onClick={() => act(t, "unmatch")} className="text-slate-400 hover:text-slate-900" aria-label="Undo"><Undo2 size={15} /></button>
                          )}
                          {t.status !== "matched" && (
                            <button data-testid="delete-transaction-button" onClick={async () => { try { await api.delete(`/bank/transactions/${t.id}`); load(); } catch (err) { toast.error(apiError(err)); } }}
                              className="text-slate-400 hover:text-rose-600" aria-label="Delete"><Trash2 size={15} /></button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty message={txns.length ? `No ${filter} lines.` : "No statement imported for this account yet. Import a CSV export from your bank, MTN MoMo or Airtel Money."} />}
      </Panel>

      {importOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/50 p-4">
          <div className="mx-auto w-full max-w-3xl rounded-lg border border-slate-200 bg-white p-5 shadow-md" data-testid="import-dialog">
            <h3 className="font-display text-base font-bold text-slate-900">Import statement into {acc?.name}</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Statement format</label>
                <select data-testid="import-template-select" value={template} onChange={(e) => { setTemplate(e.target.value); if (csv) setMapping(guess(csv.headers, TEMPLATES[e.target.value].hints)); }}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                  {Object.entries(TEMPLATES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">CSV file</label>
                <input data-testid="import-file-input" type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files[0])}
                  className="w-full text-sm" />
              </div>
            </div>
            {csv && (
              <>
                <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Column mapping — {csv.name}</p>
                <div className="mt-2 grid gap-3 sm:grid-cols-3">
                  {FIELDS.map(([f, label]) => (
                    <div key={f}>
                      <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
                      <select data-testid={`map-${f}-select`} value={mapping[f] || NONE} onChange={(e) => setMapping({ ...mapping, [f]: e.target.value })}
                        className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs">
                        <option value={NONE}>— not in file —</option>
                        {csv.headers.map((h, i) => <option key={i} value={String(i)}>{h || `Column ${i + 1}`}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Preview · {preview.length} valid lines of {csv.rows.length}{csv.rows.length - preview.length > 0 && ` (${csv.rows.length - preview.length} skipped: no date or zero amount)`}</p>
                <div className="mt-2 max-h-56 overflow-auto rounded border border-slate-200 text-xs" data-testid="import-preview">
                  <table className="w-full">
                    <tbody>
                      {preview.slice(0, 50).map((r, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-2 py-1">{r.date}</td><td className="max-w-xs truncate px-2 py-1">{r.description}</td>
                          <td className="px-2 py-1 text-slate-500">{r.reference}</td>
                          <td className={`px-2 py-1 text-right tabular-nums ${r.amount > 0 ? "text-emerald-700" : "text-rose-700"}`}>{money(r.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <div className="mt-5 flex gap-2">
              <button data-testid="confirm-import-button" disabled={!preview.length} onClick={doImport}
                className="flex-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">Import {preview.length} lines</button>
              <button data-testid="cancel-import-button" onClick={() => { setImportOpen(false); setCsv(null); }}
                className="flex-1 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {createFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <form onSubmit={submitCreate} className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-md" data-testid="record-dialog">
            <h3 className="font-display text-base font-bold text-slate-900">Record {createFor.amount > 0 ? "money in" : "money out"} · {ugx(Math.abs(createFor.amount))}</h3>
            <p className="mt-1 truncate text-sm text-slate-500">{shortDate(createFor.date)} · {createFor.description}</p>
            <label className="mb-1.5 mt-4 block text-sm font-medium text-slate-700">Record as</label>
            <select data-testid="record-kind-select" value={createForm.kind} onChange={(e) => setCreateForm({ ...createForm, kind: e.target.value, ref_id: "" })}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
              {createFor.amount > 0 ? <option value="invoice_payment">Customer payment against an invoice</option>
                : <><option value="expense">Expense</option><option value="bill_payment">Payment of a supplier bill</option></>}
            </select>
            {createForm.kind === "invoice_payment" && (
              <select data-testid="record-invoice-select" required value={createForm.ref_id} onChange={(e) => setCreateForm({ ...createForm, ref_id: e.target.value })}
                className="mt-3 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                <option value="">Select unpaid invoice…</option>
                {openDocs.invoices.map((i) => <option key={i.id} value={i.id}>{i.number} · {i.customer_name} · due {money(i.amount_due)}</option>)}
              </select>
            )}
            {createForm.kind === "bill_payment" && (
              <select data-testid="record-bill-select" required value={createForm.ref_id} onChange={(e) => setCreateForm({ ...createForm, ref_id: e.target.value })}
                className="mt-3 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                <option value="">Select unpaid bill…</option>
                {openDocs.bills.map((b) => <option key={b.id} value={b.id}>{b.number} · {b.vendor_name} · due {money(b.amount_due)}</option>)}
              </select>
            )}
            {createForm.kind === "expense" && (
              <>
                <select data-testid="record-expense-account-select" required value={createForm.account_id} onChange={(e) => setCreateForm({ ...createForm, account_id: e.target.value })}
                  className="mt-3 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                  <option value="">Select expense category…</option>
                  {expenseAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                </select>
                <select data-testid="record-vat-select" value={createForm.vat_rate} onChange={(e) => setCreateForm({ ...createForm, vat_rate: e.target.value })}
                  className="mt-3 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                  <option value={0}>No VAT</option><option value={18}>Amount includes 18% VAT</option>
                </select>
              </>
            )}
            <div className="mt-5 flex gap-2">
              <button data-testid="save-record-button" className="flex-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Record & match</button>
              <button type="button" data-testid="cancel-record-button" onClick={() => setCreateFor(null)} className="flex-1 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
